// Inventory module API helpers
import { api, backendOrigin } from "@/lib/api";

// ---- Init / reconcile ----
export async function initInventory() {
  const { data } = await api.post("/inventory/init");
  return data;
}
export async function reconcileAll() {
  const { data } = await api.post("/inventory/reconcile/all");
  return data;
}

/* Fulfillment ledger (since 2026-05-26) */
export async function fulfillmentSyncNow() {
  const { data } = await api.post("/inventory/sync-now");
  return data;
}
export async function fulfillmentFullRebuild() {
  const { data } = await api.post("/inventory/recompute");
  return data;
}
export async function getItemAudit(itemId, limit = 500) {
  const { data } = await api.get(`/inventory/items/${encodeURIComponent(itemId)}/audit`, { params: { limit } });
  return data;
}
export async function getLedgerStatus() {
  const { data } = await api.get("/inventory/ledger-status");
  return data;
}

/* Reservation tags settings (since 2026-02-16) */
export async function getReservationTags() {
  const { data } = await api.get("/inventory/settings/reservation-tags");
  return data;
}
export async function updateReservationTags(tags) {
  const { data } = await api.put("/inventory/settings/reservation-tags", { tags });
  return data;
}
export async function autocompleteOrderTags(q = "", limit = 30) {
  const { data } = await api.get("/inventory/settings/order-tags/autocomplete", { params: { q, limit } });
  return data;
}

// ---- Suppliers ----
export async function listSuppliers() {
  const { data } = await api.get("/inventory/suppliers");
  return data;
}
export async function createSupplier(body) {
  const { data } = await api.post("/inventory/suppliers", body);
  return data;
}
export async function updateSupplier(id, body) {
  const { data } = await api.put(`/inventory/suppliers/${id}`, body);
  return data;
}
export async function deleteSupplier(id) {
  await api.delete(`/inventory/suppliers/${id}`);
}

// ---- Items ----
export async function listItems(params = {}) {
  const { data } = await api.get("/inventory/items", { params });
  return data;
}
export async function getItem(id) {
  const { data } = await api.get(`/inventory/items/${id}`);
  return data;
}
export async function updateItem(id, body) {
  const { data } = await api.put(`/inventory/items/${id}`, body);
  return data;
}
export async function adjustStock(id, { delta, set_to, notes }) {
  const { data } = await api.post(`/inventory/items/${id}/adjust`, { delta, set_to, notes });
  return data;
}

// ---- Stock-in ----
export async function stockIn(body) {
  const { data } = await api.post("/inventory/stock-in", body);
  return data;
}

// ---- Movements ----
export async function listMovements(params = {}) {
  const { data } = await api.get("/inventory/movements", { params });
  return data;
}

// ---- Purchase lists ----
export async function generatePurchaseLists(supplier_id = null) {
  const { data } = await api.post("/inventory/purchase-lists/generate", { supplier_id });
  return data;
}
export async function listPurchaseLists(params = {}) {
  const { data } = await api.get("/inventory/purchase-lists", { params });
  return data;
}
export async function getPurchaseList(id) {
  const { data } = await api.get(`/inventory/purchase-lists/${id}`);
  return data;
}
export async function setPurchaseListStatus(id, status) {
  const { data } = await api.put(`/inventory/purchase-lists/${id}/status`, { status });
  return data;
}
export async function deletePurchaseList(id) {
  await api.delete(`/inventory/purchase-lists/${id}`);
}
export function purchaseListCsvUrl(id) {
  const base = backendOrigin();
  return `${base}/api/inventory/purchase-lists/${id}/export.csv`;
}

// ---- Analytics ----
export async function getSummary() {
  const { data } = await api.get("/inventory/analytics/summary");
  return data;
}
export async function getTopSelling(days = 30, limit = 10) {
  const { data } = await api.get("/inventory/analytics/top-selling", { params: { days, limit } });
  return data;
}
export async function getDeadStock(days = 60, limit = 50) {
  const { data } = await api.get("/inventory/analytics/dead-stock", { params: { days, limit } });
  return data;
}

// ---- Monthly snapshots ----
export async function listSnapshots() {
  const { data } = await api.get("/inventory/snapshots");
  return data;
}
export async function generateSnapshot(year, month) {
  const { data } = await api.post(`/inventory/snapshots/generate?year=${year}&month=${month}`);
  return data;
}
export async function getSnapshot(id, params = {}) {
  const { data } = await api.get(`/inventory/snapshots/${id}`, { params });
  return data;
}
export async function deleteSnapshot(id) {
  await api.delete(`/inventory/snapshots/${id}`);
}
// ---- Bundles ----
export async function listBundles() {
  const { data } = await api.get("/inventory/bundles");
  return data;
}
export async function createBundle(body) {
  const { data } = await api.post("/inventory/bundles", body);
  return data;
}
export async function updateBundle(id, body) {
  const { data } = await api.put(`/inventory/bundles/${id}`, body);
  return data;
}
export async function deleteBundle(id) {
  await api.delete(`/inventory/bundles/${id}`);
}

// ---- Bulk items ----
export async function bulkUpdateItems(body) {
  const { data } = await api.post("/inventory/items/bulk", body);
  return data;
}

// ---- Restock from return ----
export async function restockFromReturn(body) {
  const { data } = await api.post("/inventory/restock-from-return", body);
  return data;
}

export async function downloadSnapshotXlsx(id, period) {
  const r = await api.get(`/inventory/snapshots/${id}/export.xlsx`, { responseType: "blob" });
  _saveBlob(r.data, `stoc_${period}.xlsx`);
}
export async function downloadSnapshotPdf(id, period) {
  const r = await api.get(`/inventory/snapshots/${id}/export.pdf`, { responseType: "blob" });
  _saveBlob(r.data, `stoc_${period}.pdf`);
}

function _saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
